import React, { useEffect, useState, ChangeEvent, KeyboardEvent } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import {
  makeGETRequest,
  makePOSTRequest,
  makePUTRequest,
  makeDELETERequest,
} from "../Api/api";
import toastr from "toastr";

interface EmailList {
  uuid: string;
  label: string;
}

interface EmailListDetail {
  uuid: string;
  label: string;
  emails: string[];
}

const EmailListManager: React.FC = () => {
  const [lists, setLists] = useState<EmailList[]>([]);
  const [selectedUuid, setSelectedUuid] = useState<string>("");
  const [detail, setDetail] = useState<EmailListDetail | null>(null);

  const [openCreate, setOpenCreate] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newEmails, setNewEmails] = useState<string[]>([]);
  const [newEmailInput, setNewEmailInput] = useState("");

  const [openSend, setOpenSend] = useState(false);
  const [sendSubject, setSendSubject] = useState("");
  const [sendFile, setSendFile] = useState<File | null>(null);
  const [sendListUuid, setSendListUuid] = useState("");

  const fetchLists = () => {
    makeGETRequest("admin/quote_desk/email_lists/")
      .then((resp) => setLists(resp.data))
      .catch((err) => toastr.error(err.message));
  };

  useEffect(() => {
    fetchLists();
  }, []);

  useEffect(() => {
    if (selectedUuid) {
      makeGETRequest(`admin/quote_desk/email_lists_retrieve/${selectedUuid}/`)
        .then((resp) =>
          setDetail({
            ...resp.data.data,
            emails: resp.data.data.emails.split(","),
          })
        )
        .catch((err) => toastr.error(err.message));
    } else {
      setDetail(null);
    }
  }, [selectedUuid]);

  const handleCreate = () => {
    const emailsString = newEmails.join(",");
    makePOSTRequest("admin/quote_desk/email_lists_create/", {
      label: newLabel,
      emails: emailsString,
    })
      .then(() => {
        toastr.success("List created");
        setOpenCreate(false);
        setNewLabel("");
        setNewEmails([]);
        setNewEmailInput("");
        fetchLists();
      })
      .catch((err) => toastr.error(err.message));
  };

  const handleNewEmailKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && newEmailInput.trim()) {
      setNewEmails((prev) => [...prev, newEmailInput.trim()]);
      setNewEmailInput("");
      e.preventDefault();
    }
  };

  const handleNewEmailDelete = (email: string) => {
    setNewEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleRemoveEmail = (email: string) => {
    if (!detail) return;
    makePUTRequest(`admin/quote_desk/remove_email/${detail.uuid}/`, {
      emails: [email],
    })
      .then(() => {
        toastr.success("Email removed");
        setDetail((prev) =>
          prev
            ? { ...prev, emails: prev.emails.filter((e) => e !== email) }
            : prev
        );
      })
      .catch((err) => toastr.error(err.message));
  };

  const handleAddEmail = () => {
    if (!detail || !newEmailInput.trim()) return;
    makePUTRequest(`admin/quote_desk/email_lists_update/${detail.uuid}/`, {
      emails: [...detail.emails, newEmailInput.trim()],
    })
      .then(() => {
        toastr.success("Email added");
        setDetail((prev) =>
          prev
            ? { ...prev, emails: [...prev.emails, newEmailInput.trim()] }
            : prev
        );
        setNewEmailInput("");
      })
      .catch((err) => toastr.error(err.message));
  };

  const handleDeleteList = () => {
    if (!detail) return;
    makeDELETERequest(`admin/quote_desk/email_lists_delete/${detail.uuid}/`)
      .then(() => {
        toastr.success("List deleted");
        setSelectedUuid("");
        fetchLists();
      })
      .catch((err) => toastr.error(err.message));
  };

  const handleSend = () => {
    if (!sendFile) {
      toastr.error("Please select a file");
      return;
    }
    const formData = new FormData();
    formData.append("sub_line", sendSubject);
    formData.append("email_list_uuid", sendListUuid);
    formData.append("file", sendFile);

    makePOSTRequest("admin/quote_desk/send_email_attachment/", formData)
      .then(() => {
        toastr.success("Email sent");
        setOpenSend(false);
        setSendSubject("");
        setSendFile(null);
      })
      .catch((err) => toastr.error(err.message));
  };

  return (
    <Box sx={{ p: 2, maxWidth: 600, mx: "auto" }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Email List Manager
      </Typography>

      <Button
        variant="contained"
        onClick={() => setOpenCreate(true)}
        sx={{ mb: 2 }}
      >
        Create New List
      </Button>
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)}>
        <DialogTitle>Create Email List</DialogTitle>
        <DialogContent>
          <TextField
            label="Label"
            fullWidth
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography>Enter Emails:</Typography>
          <TextField
            fullWidth
            placeholder="Type email and press Enter"
            value={newEmailInput}
            onChange={(e) => setNewEmailInput(e.target.value)}
            onKeyDown={handleNewEmailKeyDown}
            sx={{ mb: 1 }}
          />
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {newEmails.map((email) => (
              <Chip
                key={email}
                label={email}
                onDelete={() => handleNewEmailDelete(email)}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="list-select-label">Select List</InputLabel>
        <Select
          labelId="list-select-label"
          value={selectedUuid}
          label="Select List"
          onChange={(e) => setSelectedUuid(e.target.value)}
        >
          {lists.map((l) => (
            <MenuItem key={l.uuid} value={l.uuid}>
              {l.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {detail && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button color="error" onClick={handleDeleteList}>
              Delete List
            </Button>
            <Button onClick={() => setOpenSend(true)}>Initiate Email</Button>
          </Box>
          <br />
          <Typography variant="subtitle1">Add Emails to this List:</Typography>
          <TextField
            fullWidth
            placeholder="Type email and press Enter to add"
            value={newEmailInput}
            onChange={(e) => setNewEmailInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleAddEmail();
                e.preventDefault();
              }
            }}
          />
          <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ my: 1 }}>
            {detail.emails.map((email) => (
              <Chip
                key={email}
                label={email}
                onDelete={() => handleRemoveEmail(email)}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Dialog open={openSend} onClose={() => setOpenSend(false)}>
        <DialogTitle>Send Attachment</DialogTitle>
        <DialogContent>
          <TextField
            label="Subject"
            fullWidth
            value={sendSubject}
            onChange={(e) => setSendSubject(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="send-list-label">List</InputLabel>
            <Select
              labelId="send-list-label"
              value={sendListUuid}
              label="List"
              onChange={(e) => setSendListUuid(e.target.value)}
            >
              {lists.map((l) => (
                <MenuItem key={l.uuid} value={l.uuid}>
                  {l.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" component="label">
            Choose File
            <input
              type="file"
              hidden
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                if (e.target.files) setSendFile(e.target.files[0]);
              }}
            />
          </Button>
          {sendFile && <Typography sx={{ mt: 1 }}>{sendFile.name}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSend(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSend}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailListManager;
